/** biome-ignore-all lint/suspicious/noExplicitAny: ok */

import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Descendant } from 'slate';
import {
  CardID,
  DateISO,
  DayID,
  DividerID,
  EventID,
  ListID,
  TripID,
} from '../redux/trip/trip.types';
import { deepCleanObject } from '../redux/utils';
import { db, functions } from './firebase';
import {
  CardData,
  DayData,
  DeletableRolesMap,
  DividerData,
  EventData,
  ListData,
  NewTripData,
  TripRole,
  UserToRole,
} from './firebase.types';

// ─── Trips ───────────────────────────────────────────────────────────────────

export const getTripsForUser = async (userId: string) => {
  const tripsRef = collection(db, 'trips');
  const q = query(tripsRef, where(`roles.${userId}`, 'in', ['owner', 'editor', 'viewer']));
  return getDocs(q);
};

export const getTripDocument = async (id: TripID) => {
  const tripRef = doc(db, 'trips', id);
  return getDoc(tripRef);
};

export const getTripMembersDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'members'));

export const updateUserRoleInTrip = async (tripId: TripID, userId: string, role: TripRole) => {
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, { [`roles.${userId}`]: role });
};

export const removeUserFromTrip = async (tripId: TripID, userId: string) => {
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, { [`roles.${userId}`]: deleteField() });
};

export const updateRolesInTrip = async (
  tripId: TripID,
  rolesToUpdate: UserToRole[],
  usersToRemove: string[]
) => {
  const tripRef = doc(db, 'trips', tripId);
  const roles = rolesToUpdate.reduce((previousValue, currentValue) => {
    const newValue = {
      ...previousValue,
      [`roles.${currentValue.userId}`]: currentValue.role,
    };
    return newValue;
  }, {} as DeletableRolesMap);
  usersToRemove.forEach((userId) => {
    roles[`roles.${userId}`] = deleteField();
  });
  await updateDoc(tripRef, roles);
};

const createListObject = (name: string, emoji: string) => ({
  name,
  emoji,
  createdAt: serverTimestamp(),
});

export const createTripDocumentAndLists = async (userId: string, data: NewTripData) => {
  const batch = writeBatch(db);

  const newTripRef = doc(collection(db, 'trips'));
  const ownerMemberRef = doc(db, 'trips', newTripRef.id, 'members', userId);
  const foodAndDrinksListRef = doc(collection(db, 'trips', newTripRef.id, 'lists'));
  const thingsToDoListRef = doc(collection(db, 'trips', newTripRef.id, 'lists'));
  const lodgingRef = doc(collection(db, 'trips', newTripRef.id, 'lists'));
  const listOrder = [foodAndDrinksListRef.id, thingsToDoListRef.id, lodgingRef.id];

  batch.set(newTripRef, {
    roles: { [userId]: 'owner' },
    public: false,
    listOrder,
    ...data,
    createdAt: serverTimestamp(),
  });
  // Additive alongside roles above — not a replacement (see TripMember).
  batch.set(ownerMemberRef, {
    userId,
    role: TripRole.OWNER,
    joinedAt: serverTimestamp(),
    invitedBy: null,
  });
  batch.set(foodAndDrinksListRef, createListObject('Food and drinks', '🍲'));
  batch.set(thingsToDoListRef, createListObject('Things to do', '🚲'));
  batch.set(lodgingRef, createListObject('Lodging', '🏨'));

  await batch.commit();

  return getDoc(newTripRef);
};

export const deleteTripDocument = async (tripId: TripID) => {
  const recursiveDeleteTrip = httpsCallable(functions, 'recursiveDeleteTrip');
  recursiveDeleteTrip({ tripId, path: '' });
};

// ─── Lists ───────────────────────────────────────────────────────────────────

export const getListsDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'lists'));

export const updateTripField = async (tripId: TripID, field: string, value: any) => {
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, { [field]: value });
};

export const createListInTrip = async (tripId: TripID, list: ListData, listOrder: ListID[]) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const newListRef = doc(collection(db, 'trips', tripId, 'lists'));

  batch.set(newListRef, {
    ...list,
    createdAt: serverTimestamp(),
  });
  batch.update(tripRef, { listOrder: [newListRef.id].concat(listOrder) });
  await batch.commit();

  return getDoc(newListRef);
};

export const updateListInTrip = async (
  tripId: TripID,
  listId: ListID,
  field: string,
  value: any
) => {
  const listRef = doc(db, 'trips', tripId, 'lists', listId);
  await updateDoc(listRef, { [field]: value });
};

export const removeListInTrip = async (
  tripId: TripID,
  listIdToRemove: ListID,
  listOrder: ListID[],
  cardIds: CardID[],
  eventIds: EventID[],
  daysToUpdate: { [key: DayID]: EventID[] }
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const listRef = doc(db, 'trips', tripId, 'lists', listIdToRemove);

  batch.delete(listRef);
  batch.update(tripRef, { listOrder: listOrder.filter((listId) => listId !== listIdToRemove) });
  Object.keys(daysToUpdate).forEach((dayId) => {
    batch.update(doc(db, 'trips', tripId, 'days', dayId), { eventOrder: daysToUpdate[dayId] });
  });
  cardIds.forEach((cardId) => {
    batch.delete(doc(db, 'trips', tripId, 'cards', cardId));
  });
  eventIds.forEach((eventId) => {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  });
  await batch.commit();
};

// ─── Divider Firebase Utils ───────────────────────────────────────────────────

export const getDividersDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'dividers'));

export const createDividerInTrip = async (
  tripId: TripID,
  divider: DividerData,
  listOrder: ListID[]
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const newDividerRef = doc(collection(db, 'trips', tripId, 'dividers'));

  batch.set(newDividerRef, { ...divider, createdAt: serverTimestamp() });
  batch.update(tripRef, { listOrder: [newDividerRef.id].concat(listOrder) });
  await batch.commit();

  return getDoc(newDividerRef);
};

// Called when the divider "+" button creates a list. Writes only to divider.listOrder —
// NOT trip.listOrder — so the new list stays scoped inside the divider.
export const createListInDivider = async (
  tripId: TripID,
  dividerId: DividerID,
  list: ListData,
  dividerListOrder: ListID[]
) => {
  const batch = writeBatch(db);
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);
  const newListRef = doc(collection(db, 'trips', tripId, 'lists'));

  batch.set(newListRef, { ...list, createdAt: serverTimestamp() });
  batch.update(dividerRef, { listOrder: [newListRef.id].concat(dividerListOrder) });
  await batch.commit();

  return getDoc(newListRef);
};

export const updateDividerInTrip = async (
  tripId: TripID,
  dividerId: DividerID,
  field: string,
  value: any
) => {
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);
  await updateDoc(dividerRef, { [field]: value });
};

export const removeDividerInTrip = async (
  tripId: TripID,
  dividerId: DividerID,
  tripListOrder: ListID[],
  listIdsInDivider: ListID[],
  cardIds: CardID[],
  eventIds: EventID[],
  daysToUpdate: { [key: DayID]: EventID[] }
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);

  batch.delete(dividerRef);
  batch.update(tripRef, { listOrder: tripListOrder.filter((id) => id !== dividerId) });
  listIdsInDivider.forEach((listId) => {
    batch.delete(doc(db, 'trips', tripId, 'lists', listId));
  });
  cardIds.forEach((cardId) => {
    batch.delete(doc(db, 'trips', tripId, 'cards', cardId));
  });
  eventIds.forEach((eventId) => {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  });
  Object.keys(daysToUpdate).forEach((dayId) => {
    batch.update(doc(db, 'trips', tripId, 'days', dayId), { eventOrder: daysToUpdate[dayId] });
  });
  await batch.commit();
};

// Called when deleting a single list inside a divider. Patches divider.listOrder —
// NOT trip.listOrder — because divider-nested lists are absent from trip.listOrder.
export const removeListFromDivider = async (
  tripId: TripID,
  listId: ListID,
  dividerId: DividerID,
  dividerListOrder: ListID[],
  cardIds: CardID[],
  eventIds: EventID[],
  daysToUpdate: { [key: DayID]: EventID[] }
) => {
  const batch = writeBatch(db);
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);
  const listRef = doc(db, 'trips', tripId, 'lists', listId);

  batch.delete(listRef);
  batch.update(dividerRef, { listOrder: dividerListOrder.filter((id) => id !== listId) });
  cardIds.forEach((cardId) => {
    batch.delete(doc(db, 'trips', tripId, 'cards', cardId));
  });
  eventIds.forEach((eventId) => {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  });
  Object.keys(daysToUpdate).forEach((dayId) => {
    batch.update(doc(db, 'trips', tripId, 'days', dayId), { eventOrder: daysToUpdate[dayId] });
  });
  await batch.commit();
};

export const saveDividerListOrder = async (
  tripId: TripID,
  dividerId: DividerID,
  listOrder: ListID[]
) => {
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);
  await updateDoc(dividerRef, { listOrder });
};

export const moveListToDivider = async (
  tripId: TripID,
  listId: ListID,
  tripListOrder: ListID[],
  targetDividerId: DividerID,
  targetDividerListOrder: ListID[],
  targetIndex: number
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const targetDividerRef = doc(db, 'trips', tripId, 'dividers', targetDividerId);

  const newTripListOrder = tripListOrder.filter((id) => id !== listId);
  const newDividerListOrder = [...targetDividerListOrder];
  newDividerListOrder.splice(targetIndex, 0, listId);

  batch.update(tripRef, { listOrder: newTripListOrder });
  batch.update(targetDividerRef, { listOrder: newDividerListOrder });
  await batch.commit();
};

export const moveListOutOfDivider = async (
  tripId: TripID,
  listId: ListID,
  dividerId: DividerID,
  dividerListOrder: ListID[],
  tripListOrder: ListID[],
  targetIndex: number
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);
  const dividerRef = doc(db, 'trips', tripId, 'dividers', dividerId);

  const newDividerListOrder = dividerListOrder.filter((id) => id !== listId);
  const newTripListOrder = [...tripListOrder];
  newTripListOrder.splice(targetIndex, 0, listId);

  batch.update(dividerRef, { listOrder: newDividerListOrder });
  batch.update(tripRef, { listOrder: newTripListOrder });
  await batch.commit();
};

export const moveListBetweenDividers = async (
  tripId: TripID,
  listId: ListID,
  fromDividerId: DividerID,
  fromDividerListOrder: ListID[],
  toDividerId: DividerID,
  toDividerListOrder: ListID[],
  targetIndex: number
) => {
  const batch = writeBatch(db);
  const fromDividerRef = doc(db, 'trips', tripId, 'dividers', fromDividerId);
  const toDividerRef = doc(db, 'trips', tripId, 'dividers', toDividerId);

  const newFromListOrder = fromDividerListOrder.filter((id) => id !== listId);
  const newToListOrder = [...toDividerListOrder];
  newToListOrder.splice(targetIndex, 0, listId);

  batch.update(fromDividerRef, { listOrder: newFromListOrder });
  batch.update(toDividerRef, { listOrder: newToListOrder });
  await batch.commit();
};

// ─── Cards ───────────────────────────────────────────────────────────────────

export const getCardsDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'cards'));

export const createCardInList = async (
  tripId: TripID,
  listId: ListID,
  card: CardData,
  cardOrder: CardID[]
) => {
  const batch = writeBatch(db);
  const listRef = doc(db, 'trips', tripId, 'lists', listId);
  const newCardRef = doc(collection(db, 'trips', tripId, 'cards'));

  // Clean card data to remove any undefined values (Firestore doesn't accept undefined)
  const cleanedCard = deepCleanObject(card);

  // Ensure dateAdded is set to serverTimestamp
  const cardWithDateAdded = {
    ...cleanedCard,
    dateAdded: serverTimestamp(),
  };

  batch.set(newCardRef, cardWithDateAdded);
  batch.update(listRef, { cardOrder: [newCardRef.id].concat(cardOrder) });
  await batch.commit();

  return getDoc(newCardRef);
};

export const deleteCardFromList = async (
  tripId: TripID,
  listId: ListID,
  cardIdToRemove: CardID,
  cardOrder: CardID[],
  eventIdsToDelete: EventID[],
  daysToUpdate: { dayId: DayID; eventOrder: EventID[] }[]
) => {
  const batch = writeBatch(db);
  const listRef = doc(db, 'trips', tripId, 'lists', listId);
  const cardRef = doc(db, 'trips', tripId, 'cards', cardIdToRemove);

  batch.delete(cardRef);
  batch.update(listRef, { cardOrder: cardOrder.filter((cardId) => cardId !== cardIdToRemove) });
  for (const eventId of eventIdsToDelete) {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  }
  for (const day of daysToUpdate) {
    batch.update(doc(db, 'trips', tripId, 'days', day.dayId), {
      eventOrder: day.eventOrder,
    });
  }
  await batch.commit();
};

export const moveCard = async (
  tripId: TripID,
  cardIdToMove: CardID,
  fromListId: ListID,
  toListId: ListID,
  fromListCardOrder: CardID[],
  toListCardOrder: CardID[]
) => {
  const batch = writeBatch(db);
  const fromListRef = doc(db, 'trips', tripId, 'lists', fromListId);
  const toListRef = doc(db, 'trips', tripId, 'lists', toListId);

  batch.update(fromListRef, {
    cardOrder: fromListCardOrder.filter((cardId) => cardId !== cardIdToMove),
  });
  batch.update(toListRef, { cardOrder: toListCardOrder.concat([cardIdToMove]) });
  await batch.commit();
};

export const updateCardInTrip = async (
  tripId: TripID,
  cardId: CardID,
  field: string,
  value: any
) => {
  const cardRef = doc(db, 'trips', tripId, 'cards', cardId);
  await updateDoc(cardRef, { [field]: value });
};

export const deleteFieldInCardInTrip = async (tripId: TripID, cardId: CardID, field: string) => {
  const cardRef = doc(db, 'trips', tripId, 'cards', cardId);
  await updateDoc(cardRef, { [field]: deleteField() });
};

export const updateNotesForCard = async (tripId: TripID, cardId: CardID, notes: Descendant[]) => {
  const cardRef = doc(db, 'trips', tripId, 'cards', cardId);
  // Clean notes to remove any undefined values from Slate data
  const cleanedNotes = deepCleanObject(notes);
  await updateDoc(cardRef, { notes: cleanedNotes });
};

export const updateCardOrderInList = async (
  tripId: TripID,
  listId: ListID,
  cardOrder: CardID[]
) => {
  const listRef = doc(db, 'trips', tripId, 'lists', listId);
  await updateDoc(listRef, { cardOrder });
};

// ─── Days & Events ───────────────────────────────────────────────────────────

export const updateTripDates = async (
  tripId: TripID,
  startDateISO: DateISO,
  endDateISO: DateISO,
  daysToDelete: DayID[],
  eventsToDelete: EventID[]
) => {
  const batch = writeBatch(db);
  const tripRef = doc(db, 'trips', tripId);

  batch.update(tripRef, { startDateISO, endDateISO });
  daysToDelete.forEach((dayId) => {
    batch.delete(doc(db, 'trips', tripId, 'days', dayId));
  });
  eventsToDelete.forEach((eventId) => {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  });

  await batch.commit();
};

export const createDayAndAddEventInTrip = async (
  tripId: TripID,
  day: DayData,
  event: EventData
) => {
  const batch = writeBatch(db);
  const newDayRef = doc(collection(db, 'trips', tripId, 'days'));
  const newEventRef = doc(collection(db, 'trips', tripId, 'events'));

  // Clean data to remove any undefined values
  const cleanedEvent = deepCleanObject(event);
  const cleanedDay = deepCleanObject(day);

  batch.set(newEventRef, cleanedEvent);
  batch.set(newDayRef, { ...cleanedDay, eventOrder: [newEventRef.id] });

  await batch.commit();

  return {
    dayId: newDayRef.id,
    eventId: newEventRef.id,
  };
};

export const addEventToDayInTrip = async (
  tripId: TripID,
  dayId: DayID,
  eventOrder: EventID[],
  event: EventData
) => {
  const batch = writeBatch(db);
  const dayRef = doc(db, 'trips', tripId, 'days', dayId);
  const newEventRef = doc(collection(db, 'trips', tripId, 'events'));

  // Clean event data to remove any undefined values
  const cleanedEvent = deepCleanObject(event);

  const newEventOrder = eventOrder.concat([newEventRef.id]);
  batch.update(dayRef, { eventOrder: newEventOrder });
  batch.set(newEventRef, cleanedEvent);

  await batch.commit();

  return { eventId: newEventRef.id, eventOrder: newEventOrder };
};

export const moveEventInTrip = async (
  tripId: TripID,
  fromDayId: DayID,
  toDayId: DayID,
  toDateISO: DateISO,
  eventId: EventID,
  fromDayEventOrder: EventID[],
  toDayEventOrder: EventID[]
) => {
  const batch = writeBatch(db);
  const fromDayRef = doc(db, 'trips', tripId, 'days', fromDayId);
  const toDayRef = doc(db, 'trips', tripId, 'days', toDayId);
  const eventRef = doc(db, 'trips', tripId, 'events', eventId);

  batch.update(fromDayRef, { eventOrder: fromDayEventOrder });
  batch.update(toDayRef, { eventOrder: toDayEventOrder });
  batch.update(eventRef, { startDateISO: toDateISO });

  await batch.commit();
};

export const moveEventToNewDayInTrip = async (
  tripId: TripID,
  fromDayId: DayID,
  toDateISO: DateISO,
  eventId: EventID,
  fromDayEventOrder: EventID[],
  toDayEventOrder: EventID[]
) => {
  const batch = writeBatch(db);
  const fromDayRef = doc(db, 'trips', tripId, 'days', fromDayId);
  const newDayRef = doc(collection(db, 'trips', tripId, 'days'));
  const eventRef = doc(db, 'trips', tripId, 'events', eventId);

  batch.update(fromDayRef, { eventOrder: fromDayEventOrder });
  batch.set(newDayRef, {
    dateISO: toDateISO,
    eventOrder: toDayEventOrder,
  });
  batch.update(eventRef, { startDateISO: toDateISO });

  await batch.commit();

  return { toDayId: newDayRef.id };
};

export const deleteEventInTrip = async (
  tripId: TripID,
  dayId: DayID,
  eventOrder: EventID[],
  eventId: EventID
) => {
  const batch = writeBatch(db);
  const dayRef = doc(db, 'trips', tripId, 'days', dayId);
  const eventRef = doc(db, 'trips', tripId, 'events', eventId);

  batch.update(dayRef, { eventOrder });
  batch.delete(eventRef);

  await batch.commit();
};

/** Clears all events from a single itinerary day in a single batch write. */
export const clearEventsFromDay = async (
  tripId: TripID,
  dayId: DayID,
  eventIds: EventID[]
): Promise<void> => {
  const batch = writeBatch(db);
  const dayRef = doc(db, 'trips', tripId, 'days', dayId);
  batch.update(dayRef, { eventOrder: [] });
  for (const eventId of eventIds) {
    batch.delete(doc(db, 'trips', tripId, 'events', eventId));
  }
  await batch.commit();
};

/**
 * Clears all events from every day in the itinerary.
 * Splits operations into chunks of 490 to stay under Firestore's 500-op batch limit.
 */
export const clearAllEventsFromItinerary = async (
  tripId: TripID,
  days: Record<string, { id?: string; eventOrder?: string[] }>,
  eventIds: string[]
): Promise<void> => {
  const BATCH_LIMIT = 490;

  // Firestore batches are capped at 500 operations per commit. For a large trip, the total number of ops (one update per day + one delete per event) can exceed that limit, so we flatten everything into a single array and slice it into chunks of BATCH_LIMIT.
  // Most trips will produce a single chunk; the loop is a safety net for edge cases.
  const ops: Array<{ type: 'dayUpdate' | 'eventDelete'; id: string }> = [
    ...Object.values(days)
      .filter((day) => day.id && day.eventOrder?.length)
      .map((day) => ({ type: 'dayUpdate' as const, id: day.id! })),
    ...eventIds.map((id) => ({ type: 'eventDelete' as const, id })),
  ];

  for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const op of ops.slice(i, i + BATCH_LIMIT)) {
      if (op.type === 'dayUpdate') {
        batch.update(doc(db, 'trips', tripId, 'days', op.id), { eventOrder: [] });
      } else {
        batch.delete(doc(db, 'trips', tripId, 'events', op.id));
      }
    }
    await batch.commit();
  }
};

export const updateDayInTrip = async (tripId: TripID, dayId: DayID, field: string, value: any) => {
  const dayRef = doc(db, 'trips', tripId, 'days', dayId);
  await updateDoc(dayRef, { [field]: value });
};

export const getDaysDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'days'));

export const getEventsDocs = async (tripId: TripID) =>
  getDocs(collection(db, 'trips', tripId, 'events'));

export const updateLastOpenedForUser = async (tripId: TripID, userId: string): Promise<void> => {
  const tripRef = doc(db, 'trips', tripId);
  await updateDoc(tripRef, { [`lastOpened.${userId}`]: serverTimestamp() });
};
